import { UploadFile } from "@king-ict/eupisi-ui/models";

import { MedicalExamination } from "../../../models/Request";
import { Attachment } from "../../../models/Shared/Attachment";
import { readFileAsBinaryString } from "../../../utils/fileReader";
import { appendToExistingExam } from "./utils";

interface IParams {
  exam: MedicalExamination;
  setShowUploadModal: (isOpen: boolean) => void;
  handleUpdate: (value: MedicalExamination) => void;
}

export function useMedicalExaminationFiles({
  exam,
  setShowUploadModal,
  handleUpdate,
}: IParams) {
  const appendToExistingExamForCurrentExam = appendToExistingExam(exam);

  const handleFileChange = async (files: File[]) => {
    if (!files?.length) {
      const updatedExam = appendToExistingExamForCurrentExam([], "uploadFiles");

      handleUpdate(updatedExam);
      return;
    }

    const uploadedFiles: UploadFile[] = await Promise.all(
      files.map(async (file) => ({
        fileName: file.name,
        fileData: await readFileAsBinaryString(file),
      }))
    );

    const updatedExam = appendToExistingExamForCurrentExam(
      uploadedFiles,
      "uploadFiles"
    );

    handleUpdate(updatedExam);
  };

  const handleUploadFileChange = (files: UploadFile[]) => {
    const combinedFiles = files.concat(exam.uploadFiles ?? []);
    const updatedExam = appendToExistingExamForCurrentExam(
      combinedFiles,
      "uploadFiles"
    );

    setShowUploadModal(false);
    handleUpdate(updatedExam);
  };

  const handleRemoveAttachment = (attachment: Attachment) => {
    if (!attachment?.id) {
      return;
    }

    const attachments = exam.attachments?.filter((item) => item.id !== attachment.id);
    const updatedExam = appendToExistingExamForCurrentExam(
      attachments ?? [],
      "attachments"
    );

    handleUpdate(updatedExam);
  };

  const handleRemoveUploadFile = (uploadFile: UploadFile) => {
    if (!uploadFile?.fileName) {
      return;
    }

    const uploadFiles = exam.uploadFiles?.filter(
      (item) => item.fileName !== uploadFile.fileName
    );
    const updatedExam = appendToExistingExamForCurrentExam(
      uploadFiles ?? [],
      "uploadFiles"
    );

    handleUpdate(updatedExam);
  };

  return {
    handleFileChange,
    handleUploadFileChange,
    handleRemoveAttachment,
    handleRemoveUploadFile,
  };
}
